/**
 * Servicio de contabilidad.
 *
 * Cada método llama a la API Laravel real a través de PasseriniAPI.
 * El contrato de tipos (firmas de entrada/salida) se mantiene idéntico
 * al que usaban los hooks, de modo que ningún hook ni componente cambia.
 */
import { PasseriniAPI } from '@/services/apiService';
import type {
  Moneda,
  KpisData,
  FondosContables,
  ChartDataPoint,
  CuentaBancaria,
  Movimiento,
  CategoriaMovimiento,
  BeneficiarioContable,
  CobroPendiente,
  EgresoPendiente,
  DistribucionUtilidad,
  Socio,
  CreateMovimientoPayload,
  CreateCategoriaPayload,
  CreateBeneficiarioPayload,
  CreateCajaPayload,
  ConfirmarRepartoPayload,
  PagoPayload,
  Agrupador,
  GuardarAgrupadorPayload,
  NombreItemAgrupable,
  ArbolEgresosPendientes,
  ArbolCobrosPendientes,
  ResultadoPagoAgrupado,
} from '../types';
import type { DateRangeApiParams } from '../utils/dateRange';

export const contabilidadService = {
  // ── KPIs ─────────────────────────────────────────────────────────────────────

  getKpis: async (params?: DateRangeApiParams): Promise<KpisData> => {
    const { data } = await PasseriniAPI.get('/contabilidad/kpis', { params });
    return data;
  },

  getFondos: async (): Promise<FondosContables> => {
    const { data } = await PasseriniAPI.get('/contabilidad/fondos');
    return data;
  },

  // ── Chart ─────────────────────────────────────────────────────────────────────

  getChartData: async (
    params?: DateRangeApiParams
  ): Promise<{ chartData: ChartDataPoint[]; chartDataUSD: ChartDataPoint[] }> => {
    const { data } = await PasseriniAPI.get('/contabilidad/chart', { params });
    return data;
  },

  // ── Cajas ─────────────────────────────────────────────────────────────────────

  getCajas: async (): Promise<CuentaBancaria[]> => {
    const { data } = await PasseriniAPI.get('/contabilidad/cajas');
    return data;
  },

  createCaja: async (payload: CreateCajaPayload): Promise<CuentaBancaria> => {
    const { data } = await PasseriniAPI.post('/contabilidad/cajas', payload);
    return data;
  },

  updateCaja: async (id: number, payload: Partial<CreateCajaPayload>): Promise<CuentaBancaria> => {
    const { data } = await PasseriniAPI.put(`/contabilidad/cajas/${id}`, payload);
    return data;
  },

  deleteCaja: async (id: number): Promise<void> => {
    await PasseriniAPI.delete(`/contabilidad/cajas/${id}`);
  },

  // ── Movimientos ───────────────────────────────────────────────────────────────

  listMovimientos: async (): Promise<Movimiento[]> => {
    const { data } = await PasseriniAPI.get('/contabilidad/movimientos');
    return data;
  },

  createMovimiento: async (payload: CreateMovimientoPayload): Promise<Movimiento> => {
    const { data } = await PasseriniAPI.post('/contabilidad/movimientos', payload);
    return data;
  },

  updateMovimiento: async (id: number, payload: CreateMovimientoPayload): Promise<Movimiento> => {
    const { data } = await PasseriniAPI.put(`/contabilidad/movimientos/${id}`, payload);
    return data;
  },

  deleteMovimiento: async (id: number): Promise<void> => {
    await PasseriniAPI.delete(`/contabilidad/movimientos/${id}`);
  },

  // ── Categorías ────────────────────────────────────────────────────────────────

  listCategorias: async (tipo?: 'Ingreso' | 'Egreso'): Promise<CategoriaMovimiento[]> => {
    const { data } = await PasseriniAPI.get('/contabilidad/categorias', {
      params: tipo ? { tipo } : undefined,
    });
    return data;
  },

  createCategoria: async (payload: CreateCategoriaPayload): Promise<CategoriaMovimiento> => {
    const { data } = await PasseriniAPI.post('/contabilidad/categorias', payload);
    return data;
  },

  // ── Beneficiarios / Pagadores ────────────────────────────────────────────────

  listBeneficiarios: async (): Promise<BeneficiarioContable[]> => {
    const { data } = await PasseriniAPI.get('/contabilidad/beneficiarios');
    return data;
  },

  createBeneficiario: async (payload: CreateBeneficiarioPayload): Promise<BeneficiarioContable> => {
    const { data } = await PasseriniAPI.post('/contabilidad/beneficiarios', payload);
    return data;
  },

  // ── Cobros Pendientes ─────────────────────────────────────────────────────────

  listCobrosPendientes: async (): Promise<CobroPendiente[]> => {
    const { data } = await PasseriniAPI.get('/contabilidad/cobros-pendientes');
    return data;
  },

  /**
   * Registra un pago (parcial o total) contra el total del presupuesto de un
   * trámite/moneda (cobro de escritura). El backend crea el ContabilidadPago y
   * el Movimiento Ingreso automáticamente.
   * POST /contabilidad/cobros-pendientes/escrituras/:tramiteId/:moneda/pagos
   */
  registrarCobro: async (
    tramiteId: number,
    moneda: string,
    payload: PagoPayload
  ): Promise<void> => {
    await PasseriniAPI.post(
      `/contabilidad/cobros-pendientes/escrituras/${tramiteId}/${moneda}/pagos`,
      payload
    );
  },

  /**
   * Registra un cobro en un OtroTramite desde contabilidad.
   * El backend crea el OtroTramiteCobro; el observer genera el Movimiento Ingreso.
   * POST /contabilidad/otros-tramites/:otroTramiteId/:moneda/cobros
   */
  registrarCobroOtroTramite: async (
    otroTramiteId: string,
    moneda: string,
    payload: PagoPayload
  ): Promise<void> => {
    await PasseriniAPI.post(
      `/contabilidad/otros-tramites/${otroTramiteId}/${moneda}/cobros`,
      payload
    );
  },

  // ── Egresos Pendientes ────────────────────────────────────────────────────────

  listEgresosPendientes: async (): Promise<EgresoPendiente[]> => {
    const { data } = await PasseriniAPI.get('/contabilidad/egresos-pendientes');
    return data;
  },

  /**
   * Registra un pago (parcial o total) contra un egreso pendiente.
   * El backend crea el ContabilidadPago y el Movimiento Egreso automáticamente.
   * POST /contabilidad/egresos-pendientes/:id/pagos
   */
  pagarEgreso: async (id: string, payload: PagoPayload): Promise<void> => {
    await PasseriniAPI.post(`/contabilidad/egresos-pendientes/${id}/pagos`, payload);
  },

  /** GET /contabilidad/cobros-pendientes/arbol */
  getArbolCobros: async (): Promise<ArbolCobrosPendientes> => {
    const { data } = await PasseriniAPI.get('/contabilidad/cobros-pendientes/arbol');
    return data;
  },

  /**
   * Cobra un agrupador y sus subagrupadores: el backend crea un único Movimiento
   * Ingreso e imputa del ítem más viejo al más nuevo. Con `cliente` cobra solo
   * los ítems de ese cliente; si mezcla clientes solo acepta el saldo completo.
   * POST /contabilidad/cobros-pendientes/agrupadores/:id/:moneda/cobros
   */
  cobrarAgrupador: async (
    id: number,
    moneda: Moneda,
    payload: PagoPayload,
    cliente?: string,
  ): Promise<ResultadoPagoAgrupado> => {
    const { data } = await PasseriniAPI.post(
      `/contabilidad/cobros-pendientes/agrupadores/${id}/${moneda}/cobros`,
      { ...payload, cliente }
    );
    return data;
  },

  /**
   * Cobra un único ítem de una escritura, imputado a ese ítem.
   * POST /contabilidad/cobros-pendientes/items/:id/cobros
   */
  cobrarItemEscritura: async (id: string, payload: PagoPayload): Promise<ResultadoPagoAgrupado> => {
    const { data } = await PasseriniAPI.post(`/contabilidad/cobros-pendientes/items/${id}/cobros`, payload);
    return data;
  },

  /** GET /contabilidad/egresos-pendientes/arbol */
  getArbolEgresos: async (): Promise<ArbolEgresosPendientes> => {
    const { data } = await PasseriniAPI.get('/contabilidad/egresos-pendientes/arbol');
    return data;
  },

  /**
   * Paga (total o parcial) un agrupador y sus subagrupadores: el backend crea un
   * único Movimiento Egreso e imputa del ítem más viejo al más nuevo.
   * POST /contabilidad/egresos-pendientes/agrupadores/:id/:moneda/pagos
   */
  pagarAgrupador: async (id: number, moneda: Moneda, payload: PagoPayload): Promise<ResultadoPagoAgrupado> => {
    const { data } = await PasseriniAPI.post(
      `/contabilidad/egresos-pendientes/agrupadores/${id}/${moneda}/pagos`,
      payload
    );
    return data;
  },

  // ── Agrupadores ───────────────────────────────────────────────────────────────

  listAgrupadores: async (): Promise<Agrupador[]> => {
    const { data } = await PasseriniAPI.get('/contabilidad/agrupadores');
    return data;
  },

  createAgrupador: async (payload: GuardarAgrupadorPayload): Promise<Agrupador> => {
    const { data } = await PasseriniAPI.post('/contabilidad/agrupadores', payload);
    return data;
  },

  updateAgrupador: async (id: number, payload: GuardarAgrupadorPayload): Promise<Agrupador> => {
    const { data } = await PasseriniAPI.put(`/contabilidad/agrupadores/${id}`, payload);
    return data;
  },

  deleteAgrupador: async (id: number): Promise<void> => {
    await PasseriniAPI.delete(`/contabilidad/agrupadores/${id}`);
  },

  listNombresAgrupables: async (): Promise<NombreItemAgrupable[]> => {
    const { data } = await PasseriniAPI.get('/contabilidad/agrupadores/nombres');
    return data;
  },

  /** Asocia los nombres al agrupador; con agrupadorId null los desasocia. */
  asignarNombres: async (nombres: string[], agrupadorId: number | null): Promise<void> => {
    await PasseriniAPI.put('/contabilidad/agrupadores/nombres', { nombres, agrupadorId });
  },

  // ── Historial de Utilidad ─────────────────────────────────────────────────────

  listHistorialUtilidad: async (): Promise<DistribucionUtilidad[]> => {
    const { data } = await PasseriniAPI.get('/contabilidad/historial-utilidad');
    return data;
  },

  confirmarReparto: async (payload: ConfirmarRepartoPayload): Promise<DistribucionUtilidad> => {
    const { data } = await PasseriniAPI.post('/contabilidad/reparto', payload);
    return data;
  },

  cancelarDistribucion: async (id: number): Promise<void> => {
    await PasseriniAPI.delete(`/contabilidad/reparto/${id}`);
  },

  // ── Socios ────────────────────────────────────────────────────────────────────

  listSocios: async (): Promise<Socio[]> => {
    const { data } = await PasseriniAPI.get('/contabilidad/socios');
    return data;
  },

  saveSocios: async (socios: Socio[]): Promise<Socio[]> => {
    const payload = socios.map(({ isNew, ...s }) => ({
      ...s,
      id: isNew ? null : s.id,
    }));
    const { data } = await PasseriniAPI.put('/contabilidad/socios', { socios: payload });
    return data;
  },
};
